import { UserAuthMethod } from "../../../domain/user/user";
import { UserEntity } from "../../../domain/user/user-entity";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { gqlClient, runGqlQueryV2 } from "../../../../graphql/client";
import { gqlHeader, reportUnexpectedError } from "@graffer-inc/smart-apply";
import { CloudGovApiError } from "../../../domain/api-error";

export type ChangeAuthMethodServiceProps = {
  user: UserEntity;
  changeTo: UserAuthMethod;
  externalUserID: string | null;
};

class ChangeAuthmethodServiceError extends Error {
  public constructor(message?: string) {
    super(message);
  }
}

export class InvalidAuthMethod extends ChangeAuthmethodServiceError {}
export class FailedChangeAuthMethod extends ChangeAuthmethodServiceError {}
export class FailedGetSAMLSetting extends ChangeAuthmethodServiceError {}
export class InvalidExternalUserID extends ChangeAuthmethodServiceError {}
export class UserEmailAlreadyExists extends ChangeAuthmethodServiceError {}

export class ChangeAuthMethodService {
  static async HasSAMLSetting(orgID: string) {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.orgSamlSetting({ orgID }, gqlHeader("q")),
      );
      if (res.error) {
        throw res.error;
      }

      if (res.data?.orgSamlSetting) {
        return true;
      }

      return false;
    } catch (e) {
      throw new FailedGetSAMLSetting(e);
    }
  }
  static async ChangeAuthMethod(
    user: UserEntity,
    changeTo: UserAuthMethod,
    externalUserID: string | null,
  ) {
    switch (changeTo) {
      case "password":
        try {
          await apiClient.changeAuthMethodToPassword(user.id);
        } catch (e) {
          if (CloudGovApiError.failedSendMail(e)) {
            throw new FailedChangeAuthMethod(
              "仮パスワードの送信に失敗しました。時間をおいてから、再度お試しください。",
            );
          }

          if (
            CloudGovApiError.isErrorCode(
              e,
              "org_user_with_same_email_already_exists",
            )
          ) {
            const err = new CloudGovApiError(e);
            const errResponse = err.response as any as {
              msg: string;
            };
            throw new UserEmailAlreadyExists(
              `このユーザーのメールアドレスは、すでにアカウント${errResponse.msg}に登録されているため、仮パスワードの送信ができません。`,
            );
          }

          reportUnexpectedError(e);
          throw new FailedChangeAuthMethod("認証方式の変更に失敗しました");
        }
        break;
      case "saml":
        if (!externalUserID) {
          throw new InvalidExternalUserID("外部ユーザーIDが入力されていません");
        }

        try {
          await apiClient.changeAuthMethodToSAML(user.id, externalUserID);
        } catch (e) {
          if (CloudGovApiError.externalUserIDAlreadyUsed(e)) {
            throw new InvalidExternalUserID(
              "この外部ユーザーIDは既に使用されています",
            );
          }
          reportUnexpectedError(e);
          throw new FailedChangeAuthMethod("認証方式の変更に失敗しました");
        }

        break;
      default:
        throw new InvalidAuthMethod("サポートされていない認証方式です");
    }
  }
}
